import { TellescopeModelFromName } from "@tellescope/tanstack-query";
import { AnswerForType, FormResponseValueAnswer } from "@tellescope/types-models";
import { RefObject, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { FORM_FIELD_VALIDATORS_BY_TYPE } from "@tellescope/validation";
import { convertFormFieldType, isBookingBlockedByEligibility, isValueEmpty } from "../../utils";
import { validateFieldByType } from "../validate-field-by-type";
import { translateValidatorError } from "../translate-validator-error";
import { getValueForValidation } from "./get-value-for-validation";
import { isRepeatMismatch } from "./resolve-repeat-mismatch";
import type { LocalTellescopeFormResponseMap } from "./use-tellescope-form-core";
import type { FormShallowStoreHook } from "../../store";

export interface UseFieldValidationArgs {
  fieldsMap: Record<string, TellescopeModelFromName<"form_fields">> | undefined;
  fieldsArr: TellescopeModelFromName<"form_fields">[] | undefined;
  currentResponses: LocalTellescopeFormResponseMap;
  currentResponsesRef: RefObject<LocalTellescopeFormResponseMap>;
  uncommittedValuesRef: RefObject<Record<string, FormResponseValueAnswer["value"]>>;
  pendingFiles: Record<string, File[]>;
  repeatInputs: Record<string, string>;
  isSubFieldHidden: (fieldId: string) => boolean;
  setTouchedFields: (touchedFields: Set<string>) => void;
  useFormShallowStore: FormShallowStoreHook;
}

export interface ValidateFieldArgs<TField extends keyof AnswerForType = keyof AnswerForType> {
  fieldId: string;
  valueOverride?: AnswerForType[TField];
}

export const useFieldValidation = ({
  fieldsMap,
  fieldsArr,
  currentResponses,
  currentResponsesRef,
  uncommittedValuesRef,
  pendingFiles,
  repeatInputs,
  isSubFieldHidden,
  setTouchedFields,
  useFormShallowStore,
}: UseFieldValidationArgs) => {
  const { t } = useTranslation("forms");

  const { fieldErrors, updateFieldErrors } = useFormShallowStore("fieldErrors", "updateFieldErrors");

  const syncGroupErrorForSubField = useCallback(
    (subFieldId: string) => {
      const group = fieldsArr?.find(
        (f) => f.type === "Question Group" && f.options?.subFields?.some((sf) => sf.id === subFieldId)
      );
      if (!group) return;
      updateFieldErrors((prev) => ({
        ...prev,
        [group.id]: (group.options?.subFields ?? []).reduce<string | null>(
          (firstError, sf) => firstError ?? prev[sf.id] ?? null,
          null
        ),
      }));
    },
    [fieldsArr, updateFieldErrors]
  );

  const validateFieldInternal = useCallback(
    async <TField extends keyof AnswerForType = keyof AnswerForType>(
      args: ValidateFieldArgs<TField>
    ): Promise<string | null> => {
      const fieldId = args.fieldId;
      const field = fieldsMap?.[fieldId];
      if (!field) {
        const errorMessage = t("Field not found");
        updateFieldErrors((prev) => ({
          ...prev,
          [fieldId]: errorMessage,
        }));
        return errorMessage;
      }

      const type = convertFormFieldType(field.type);
      if (type === null) {
        updateFieldErrors((prev) => ({
          ...prev,
          [fieldId]: null,
        }));
        return null;
      }

      if (isSubFieldHidden(fieldId)) {
        updateFieldErrors((prev) => ({ ...prev, [fieldId]: null }));
        return null;
      }

      // Recurses before the group's own error is written, and syncGroupErrorForSubField later reads
      // those sub-field errors out of the same state — so these writes must stay interleaved.
      if (type === "Question Group") {
        const subFields = field.options?.subFields ?? [];
        let firstError: string | null = null;
        for (const sf of subFields) {
          const subError = await validateFieldInternal({ fieldId: sf.id });
          if (subError && !firstError) firstError = subError;
        }
        updateFieldErrors((prev) => ({ ...prev, [fieldId]: firstError }));
        return firstError;
      }

      if (field.type === "Bridge Eligibility" || field.type === "Candid Eligibility") {
        updateFieldErrors((prev) => ({ ...prev, [fieldId]: null }));
        return null;
      }

      if (field.type === "Hidden Value") {
        updateFieldErrors((prev) => ({ ...prev, [fieldId]: null }));
        return null;
      }

      if (isBookingBlockedByEligibility(field, Object.values(currentResponsesRef.current))) {
        updateFieldErrors((prev) => ({ ...prev, [fieldId]: null }));
        return null;
      }

      if (field.type === "Redirect") {
        updateFieldErrors((prev) => ({ ...prev, [fieldId]: null }));
        return null;
      }

      const validator = FORM_FIELD_VALIDATORS_BY_TYPE[field.type];
      if (!validator) {
        updateFieldErrors((prev) => ({
          ...prev,
          [fieldId]: null,
        }));
        return null;
      }

      const response = currentResponsesRef.current[fieldId] ?? currentResponses[fieldId];
      const rawValue =
        "valueOverride" in args
          ? args.valueOverride
          : fieldId in uncommittedValuesRef.current
          ? uncommittedValuesRef.current[fieldId]
          : response?.answer.value;
      const value =
        (field.type === "string" || field.type === "stringLong") && typeof rawValue === "string"
          ? rawValue.trim()
          : rawValue;
      const hasPendingFiles = (pendingFiles[fieldId]?.length ?? 0) > 0;
      const isEmpty = isValueEmpty(value) && !hasPendingFiles;

      if (isRepeatMismatch({ field, value, confirmation: repeatInputs[fieldId] ?? "" })) {
        const errorMessage = t("Values do not match");
        updateFieldErrors((prev) => ({
          ...prev,
          [fieldId]: errorMessage,
        }));
        return errorMessage;
      }

      try {
        if (field.type === "multiple_choice" || field.type === "Dropdown") {
          const selectedOptions = Array.isArray(value) ? value : [];

          if (field.type === "multiple_choice" && field.options?.radio && selectedOptions.length > 1) {
            const errorMessage = t("Only 1 choice is allowed");
            updateFieldErrors((prev) => ({
              ...prev,
              [fieldId]: errorMessage,
            }));
            return errorMessage;
          }
        } else if (hasPendingFiles && isValueEmpty(value)) {
          updateFieldErrors((prev) => ({ ...prev, [fieldId]: null }));
          return null;
        } else {
          const validationType = convertFormFieldType(field.type);
          if (!validationType || validationType === "Question Group") return null;
          try {
            await validator(
              getValueForValidation<keyof AnswerForType>(validationType, value),
              field.options,
              field.isOptional
            );
          } catch (err) {
            if (!(isEmpty && field.isOptional)) {
              const rawError =
                typeof err === "object" && err !== null && "message" in err ? String(err.message) : String(err);
              const errorMessage = translateValidatorError(rawError, t);
              updateFieldErrors((prev) => ({
                ...prev,
                [fieldId]: errorMessage,
              }));
              return errorMessage;
            }
          }
        }

        // Additional per-type validation (e.g. minLength for string/stringLong, number range).
        // These are format/range checks that must run before the optional short-circuit.
        const additionalError = validateFieldByType(field, value);
        if (additionalError) {
          updateFieldErrors((prev) => ({
            ...prev,
            [fieldId]: additionalError,
          }));
          return additionalError;
        }

        // Optional short-circuit: an empty optional field is valid once format checks pass.
        if (isEmpty && field.isOptional) {
          updateFieldErrors((prev) => ({
            ...prev,
            [fieldId]: null,
          }));
          return null;
        }

        // Required-empty gate: a required field with no value errors here.
        if (isEmpty && !field.isOptional) {
          const errorMessage = t("{{title}} is required", { title: field.title });
          updateFieldErrors((prev) => ({
            ...prev,
            [fieldId]: errorMessage,
          }));
          return errorMessage;
        }

        updateFieldErrors((prev) => ({
          ...prev,
          [fieldId]: null,
        }));
        return null;
      } catch (error) {
        let rawError = "";
        if (typeof error === "object" && error !== null && "message" in error) {
          const message = error.message;
          if (typeof message === "string") {
            rawError = message;
          } else {
            rawError = String(message);
          }
        } else {
          rawError = String(error);
        }

        const errorMessage = translateValidatorError(rawError, t);
        updateFieldErrors((prev) => ({
          ...prev,
          [fieldId]: errorMessage,
        }));
        return errorMessage;
      }
    },
    [
      currentResponses,
      currentResponsesRef,
      fieldsMap,
      isSubFieldHidden,
      pendingFiles,
      repeatInputs,
      t,
      uncommittedValuesRef,
    ]
  );

  const validateField = useCallback(
    async <TField extends keyof AnswerForType = keyof AnswerForType>(
      args: ValidateFieldArgs<TField>
    ): Promise<string | null> => {
      const error = await validateFieldInternal(args);
      syncGroupErrorForSubField(args.fieldId);
      return error;
    },
    [validateFieldInternal, syncGroupErrorForSubField]
  );

  const validateAllFields = useCallback(async (): Promise<boolean> => {
    const fieldIds = Object.keys(currentResponses);
    setTouchedFields(new Set(fieldIds));
    const validationResults = await Promise.all(fieldIds.map((fieldId) => validateField({ fieldId })));
    return validationResults.every((error) => error === null);
  }, [currentResponses, validateField, setTouchedFields]);

  return { fieldErrors, updateFieldErrors, validateField, validateAllFields };
};
